import React, { useCallback, useEffect, useRef, useState } from 'react';
import { riskApi } from '../../utils/riskApi';
import { Modal } from '../review/reviewUi';

const TILES = [
  ['', 'All', 'everything proposed'],
  ['new', 'New', 'will be created'],
  ['duplicate', 'Possible duplicate', 'you decide each'],
  ['error', 'Error', 'must fix or untick'],
  ['warning', 'Warning', 'will import — check'],
  ['excluded', 'Excluded', 'unticked'],
  ['created', 'Imported', 'in Orcanos now'],
];
const PAGE = 250;
// The tile and page are kept per import, so signing out and back in lands on the same view.
const viewKey = (bid) => `tm.risk.review.${bid}`;
const readView = (bid) => {
  try { return JSON.parse(localStorage.getItem(viewKey(bid)) || '{}') || {}; } catch (_) { return {}; }
};

const STATUS_STYLE = {
  new: { background: '#DDF3E4', color: '#17603A' },
  duplicate: { background: '#FFF1D6', color: '#7A4B00' },
  error: { background: '#FBE0D5', color: '#8A2A0B' },
  warning: { background: '#E3ECFB', color: '#1F4E9E' },
  created: { background: '#5C35A8', color: '#fff' },
  failed: { background: '#7A1F05', color: '#fff' },
};

const PASS = { color: '#15803D', fontWeight: 600 };
const FAIL = { color: '#B91C1C', fontWeight: 600 };

// A finished create job's counts — what went in green, what was refused in red.
function JobResult({ job }) {
  return (
    <>
      <span style={job.created ? PASS : undefined}>{job.created} created</span>
      {job.updated ? <>, <span style={PASS}>{job.updated} updated</span></> : null}
      {job.failed ? <>, <span style={FAIL}>{job.failed} failed</span></> : null}
      {' · '}{job.traces} traces
      {job.trace_failed
        ? <span style={FAIL}>{` (${job.trace_failed} trace${job.trace_failed === 1 ? '' : 's'} refused)`}</span>
        : null}
    </>
  );
}

/**
 * Review before create (mockup screen 5) — the ONE screen every proposed risk passes through,
 * whether it came from an Excel file or from Risk Studio. Each record is shown exactly as it
 * will be written; nothing reaches Orcanos until "Create" (design R4).
 */
export default function StagingReview({ batchId, onCreated }) {
  const [filter, setFilterRaw] = useState(() => readView(batchId).filter || '');
  const [page, setPage] = useState(() => readView(batchId).page || 1);
  const [bulk, setBulk] = useState(false);
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [starting, setStarting] = useState(false);   // between the click and the job's first answer
  const poll = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await riskApi.stagingRows(batchId, { status: filter, page, limit: PAGE });
      // The page may be past the end after records were unticked out of this tile.
      if (page > 1 && d.rows.length === 0 && d.total > 0) { setPage(Math.ceil(d.total / PAGE)); return; }
      setData(d);
      setCounts(d.counts);
    } catch (e) {
      setError(e.message);
    }
  }, [batchId, filter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    try { localStorage.setItem(viewKey(batchId), JSON.stringify({ filter, page })); } catch (_) { /* private mode */ }
  }, [batchId, filter, page]);

  const setFilter = (k) => { setFilterRaw(k); setPage(1); };

  // Select / unselect all — `ids` = this page only; without it, every page of the tile.
  const includeMany = async (included, ids) => {
    setBulk(true);
    setError('');
    try {
      await riskApi.includeRows(batchId, { included, status: filter, ids });
      await load();
    } catch (e) { setError(e.message); } finally { setBulk(false); }
  };

  // Resume a job that is already running (the page may have been reloaded — Note #39).
  const watch = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const s = await riskApi.importState(batchId);
        setJob(s.job || null);
        if (!s.job || !s.job.running) {
          clearInterval(poll.current);
          load();
          if (s.job && onCreated) onCreated(s.job);
        }
      } catch (_) { /* keep polling; the job runs server-side regardless */ }
    }, 1000);
  }, [batchId, load, onCreated]);

  useEffect(() => {
    riskApi.importState(batchId).then((s) => {
      if (s.job) { setJob(s.job); if (s.job.running) watch(); }
    }).catch(() => {});
    return () => clearInterval(poll.current);
  }, [batchId, watch]);

  const patch = async (row, body) => {
    try {
      const r = await riskApi.patchRow(batchId, row.id, body);
      setData((d) => ({ ...d, rows: d.rows.map((x) => (x.id === row.id ? r.row : x)) }));
      setCounts(r.counts);
    } catch (e) { setError(e.message); }
  };

  const create = async (scope) => {
    if (starting || running) return;
    if (scope === 'all' && !window.confirm(`Import all ${importable} remaining records into Orcanos? `
      + (c.created ? `The ${c.created} already imported are skipped. ` : '')
      + 'Records with errors and undecided duplicates still wait.')) return;
    setError('');
    setStarting(true);
    try {
      const r = await riskApi.createAll(batchId, scope);
      setJob(r.job);
      watch();
    } catch (e) { setError(e.message); load(); } finally { setStarting(false); }
  };

  const download = async () => {
    try {
      const blob = await riskApi.recordBlob(batchId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `risk-import-record-${batchId.slice(0, 8)}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e.message); }
  };

  const c = counts || {};
  const running = job?.running;
  const busy = running || starting;
  // What each button would import NOW. Imported records never count, so a finished batch
  // shows 0 and its buttons switch off instead of offering the same rows again.
  const importable = c.importable ?? 0;
  const ready = c.ready ?? 0;
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const pageRows = (data?.rows || []).filter((r) => !r.orcanos_item_id);
  const pageOn = pageRows.filter((r) => r.included).length;

  const actions = (
    <>
      <button type="button" className="drv-btn drv-btn--lg" onClick={download}>Download record</button>
      <button type="button" className="drv-btn drv-btn--lg" onClick={() => create('all')}
        disabled={busy || !(importable > 0)}
        title={importable > 0 ? 'Ticks every remaining record on every page, then imports all that are ready. Already imported records are skipped.'
          : 'Nothing left to import'}>
        Import all ({importable})
      </button>
      <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => create('selected')}
        disabled={busy || !(ready > 0)}
        title={ready > 0 ? 'Imports only the ticked records that are ready' : 'Nothing selected is left to import'}>
        {busy ? 'Importing…' : `Import selected (${ready})`}
      </button>
    </>
  );
  const allDone = !busy && (c.created || 0) > 0 && importable === 0;

  return (
    <div className="rsk-review">
      <div className="rsk-row-between rsk-topbar">
        <span className="rsk-sub">
          {busy ? <strong>Importing into Orcanos{job?.total ? `… ${job.done} of ${job.total}` : '…'}</strong>
            : allDone ? <strong>All records are imported ({c.created}).</strong>
              : <><strong>{c.created || 0}</strong> imported · <strong>{importable}</strong> left to import</>}
          {job && !job.running && <> · Last run: <JobResult job={job} /></>}
        </span>
        <span className="rsk-bulk">{actions}</span>
      </div>

      <div className="rsk-tiles">
        {TILES.map(([k, label, hint]) => (
          <button key={k || 'all'} type="button" className={`rsk-tile${filter === k ? ' rsk-tile--on' : ''}`}
            onClick={() => setFilter(k)}>
            <span className="rsk-tile-name">{label}</span>
            <span className="rsk-tile-n">{k ? (c[k] ?? 0) : (c.all ?? 0)}</span>
            <span className="rsk-tile-hint">{hint}</span>
          </button>
        ))}
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}

      <section className="rsk-card">
        <div className="rsk-pad rsk-sub rsk-row-between" style={{ borderBottom: '1px solid #EFECF6', gap: 12, flexWrap: 'wrap' }}>
          <span>Every record exactly as it will be written. Levels marked <em>computed</em> come from your FMEA settings.</span>
          {filter !== 'created' && total > 0 && (
            <span className="rsk-bulk">
              <button type="button" className="drv-btn" disabled={bulk || busy} onClick={() => includeMany(true)}>
                Select all {total}{pages > 1 ? ' (all pages)' : ''}
              </button>
              <button type="button" className="drv-btn" disabled={bulk || busy} onClick={() => includeMany(false)}>
                Unselect all
              </button>
            </span>
          )}
        </div>
        <div className="rsk-table-wrap">
          <table className="rsk-table" style={{ minWidth: 1100 }}>
            <thead>
              <tr><th style={{ width: 34 }}>
                <input type="checkbox" aria-label="Select every record on this page" style={{ accentColor: '#5C35A8' }}
                  checked={pageRows.length > 0 && pageOn === pageRows.length}
                  ref={(el) => { if (el) el.indeterminate = pageOn > 0 && pageOn < pageRows.length; }}
                  disabled={!pageRows.length || bulk || busy}
                  onChange={(e) => includeMany(e.target.checked, pageRows.map((r) => r.id))} />
              </th><th>Source</th><th>Status</th><th>Name</th><th>Fields</th>
                <th>Traces</th><th>Note</th></tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r) => {
                const created = !!r.orcanos_item_id;
                const st = created ? 'created' : r.status;
                return (
                  <tr key={r.id} className={created ? 'rsk-imported' : undefined}
                    style={r.status === 'error' && !created ? { background: '#FFF8F5' } : undefined}>
                    <td>
                      <input type="checkbox" aria-label={`Include ${r.source_ref}`} checked={r.included || created}
                        disabled={created || busy} onChange={(e) => patch(r, { included: e.target.checked })}
                        style={{ accentColor: '#5C35A8' }} />
                    </td>
                    <td className="rsk-sub rsk-nowrap">{r.source_ref}</td>
                    <td className="rsk-nowrap"><span className="rsk-pill" style={STATUS_STYLE[st] || STATUS_STYLE.new}>{created ? 'Imported' : statusLabel(st)}</span></td>
                    <td>
                      {created ? (
                        <span className="rsk-imported-name">
                          {r.orcanos_url
                            ? <a href={r.orcanos_url} target="_blank" rel="noopener noreferrer" className="rsk-keylink"
                              title="Open in Orcanos">{r.orcanos_key}</a>
                            : <strong>{r.orcanos_key}</strong>}
                          {' '}{r.name || <em className="rsk-miss">no name</em>}
                        </span>
                      ) : (
                        <button type="button" className="rsk-cellbtn" onClick={() => setEditing(r)}>
                          {r.name || <em className="rsk-miss">no name</em>}
                        </button>
                      )}
                    </td>
                    <td className="rsk-sub">
                      {r.fields.map((f) => <span key={f.name} className="rsk-chip">{f.title}: {String(f.value)}</span>)}
                      {Object.entries(r.levels || {}).map(([k, v]) => <span key={k} className="rsk-chip rsk-chip--calc">{levelTitle(k)}: {v} · computed</span>)}
                      {r.evidence?.length > 0 && <div className="rsk-fine">Evidence: {r.evidence.map((e) => e.label).join(' · ')}</div>}
                    </td>
                    <td className="rsk-sub">
                      {r.traces.join(', ') || '—'}
                      {r.traces_missing.length > 0 && <div className="rsk-miss">skipped: {r.traces_missing.join(', ')}</div>}
                    </td>
                    <td className="rsk-sub" style={{ maxWidth: 300 }}>
                      {r.message}
                      {r.status === 'duplicate' && !created && (
                        <div className="rsk-dupbtns">
                          {[['create', 'Create anyway'], ['update', `Update ${r.dup_key}`], ['skip', 'Skip']].map(([d, label]) => (
                            <button key={d} type="button" className={`drv-btn${r.decision === d ? ' drv-btn--primary' : ''}`}
                              onClick={() => patch(r, { decision: d, included: d !== 'skip' })} disabled={busy}>{label}</button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {data && data.rows.length === 0 && (
                <tr><td colSpan={7} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>Nothing here.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="rsk-pad rsk-pager">
            <span>Records {(page - 1) * PAGE + 1}–{Math.min(page * PAGE, total)} of {total} · page {page} of {pages}</span>
            <button type="button" className="drv-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Previous</button>
            <button type="button" className="drv-btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next ›</button>
          </div>
        )}
      </section>

      <section className="rsk-card rsk-pad rsk-createbar">
        {running ? (
          <div style={{ flex: 1 }}>
            <strong>Creating in Orcanos… {job.done} of {job.total}</strong>
            <div className="rsk-trk" style={{ marginTop: 8 }}><i style={{ width: `${(job.done / Math.max(1, job.total)) * 100}%` }} /></div>
            <div className="rsk-fine">You can leave this page — it carries on in the background.</div>
          </div>
        ) : (
          <div style={{ flex: 1, fontSize: 13, lineHeight: 1.6 }}>
            {job && !job.running && (
              <div>
                <strong>Done:</strong> <JobResult job={job} />
              </div>
            )}
            <div>
              <strong>Ready: {ready}</strong> · created in Orcanos as new risks, one call each with every field.
              {(c.error || 0) > 0 && <span className="rsk-miss"> {c.error} error{c.error === 1 ? '' : 's'} must be fixed or unticked.</span>}
              {(c.duplicate || 0) > 0 && <span> Decide on each possible duplicate.</span>}
            </div>
            <div className="rsk-fine">Imported records are marked in the grid and are never imported twice.</div>
          </div>
        )}
        {actions}
      </section>

      {editing && (
        <EditRecord row={editing} onClose={() => setEditing(null)}
          onSave={async (fields) => { await patch(editing, { fields }); setEditing(null); }} />
      )}
    </div>
  );
}

function statusLabel(s) {
  return { new: 'New', duplicate: 'Possible duplicate', error: 'Error', warning: 'Warning', failed: 'Failed' }[s] || s;
}

function levelTitle(k) {
  return { CS44: 'RPN Pre', CS45: 'RPN Post' }[k] || 'Level';
}

function EditRecord({ row, onClose, onSave }) {
  const [vals, setVals] = useState(() => ({ Obj_name: row.name, ...Object.fromEntries(row.fields.map((f) => [f.name, f.value])) }));
  const titles = { Obj_name: 'Name', ...Object.fromEntries(row.fields.map((f) => [f.name, f.title])) };
  return (
    <Modal
      title={`Edit ${row.source_ref}`}
      subtitle="Changes are checked again before anything is created."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => onSave(vals)}>Save</button>
        </>
      )}
    >
      {Object.keys(vals).map((k) => (
        <label className="drv-field" key={k}>
          <span className="drv-lbl">{titles[k] || k}</span>
          <input className="drv-input" value={vals[k] || ''} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} />
        </label>
      ))}
    </Modal>
  );
}
